/**
 * Improvement Insights — Stage 1 of the Two-Stage PRIVA flow (Feb 2026).
 *
 * Workstream lifecycle:
 *   • draft     — newly generated / not yet reviewed (default state for AI output).
 *   • accepted  — consultant has approved this workstream. ONLY accepted
 *                 workstreams feed Stage 2 (Recommendation Generation).
 *   • archived  — removed from the active set. Visible in a dedicated
 *                 "Archived" sub-view (toggle in the toolbar).
 *
 * The consultant still has the full workshop toolkit (rename, merge,
 * move findings) but recommendation generation has been removed from
 * individual cards and centralised under the Recommendations tab.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Sparkles,
  RefreshCw,
  Plus,
  Pencil,
  GitMerge,
  Archive,
  ArchiveRestore,
  CheckCircle,
  Undo2,
  ChevronDown,
  ChevronUp,
  Wand2,
  MoreHorizontal,
  ArrowRightLeft,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../../../components/ui/dropdown-menu";
import { compareFindings } from "../findingOrder";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({
  baseURL: `${BACKEND_URL}/api/smartimprove`,
  withCredentials: true,
});

const PRIORITY_CHIP = {
  low: "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]",
  medium: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  high: "bg-[#FED7AA] text-[#9A3412] border-[#FDBA74]",
  critical: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
};

// Workstream status visual mapping. Legacy statuses are normalised so
// older engagements still render correctly.
const STATUS_BADGE = {
  draft: "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]",
  accepted: "bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]",
  archived: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
};

const normalizeStatus = (s) => {
  const v = (s || "").toLowerCase();
  if (v === "accepted") return "accepted";
  if (v === "archived") return "archived";
  // suggested / consultant_reviewed / approved / recommendation_ready → draft
  return "draft";
};

export default function InsightsTab({
  engagementId,
  issues,
  sites,
  isFinalized = false,
  onAIGenerate,
  aiGenerating = false,
}) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [view, setView] = useState("active"); // active | archived

  const [renameDialog, setRenameDialog] = useState(null); // {group}
  const [createDialog, setCreateDialog] = useState(false);
  const [mergeDialog, setMergeDialog] = useState(null); // {group}
  const [regenConfirm, setRegenConfirm] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ax.get(`/engagements/${engagementId}/priva/groups`);
      setGroups(r.data || []);
    } catch {
      toast.error("Failed to load workstreams");
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const confirmAndGenerate = useCallback(() => {
    if (!onAIGenerate) return;
    setRegenConfirm(true);
  }, [onAIGenerate]);

  const runGenerate = useCallback(async () => {
    setRegenConfirm(false);
    if (!onAIGenerate) return;
    await onAIGenerate();
    await reload();
  }, [onAIGenerate, reload]);

  const findingsById = useMemo(() => {
    const m = {};
    (issues || []).forEach((i) => {
      m[i.id] = i;
    });
    return m;
  }, [issues]);

  const sitesById = useMemo(() => {
    const m = {};
    (sites || []).forEach((s) => {
      m[s.id] = s.warehouse_name;
    });
    return m;
  }, [sites]);

  const activeGroups = useMemo(
    () =>
      (groups || [])
        .filter((g) => normalizeStatus(g.status) !== "archived")
        .sort((a, b) => (a.group_code || "").localeCompare(b.group_code || "")),
    [groups]
  );
  const archivedGroups = useMemo(
    () =>
      (groups || [])
        .filter((g) => normalizeStatus(g.status) === "archived")
        .sort((a, b) => (a.group_code || "").localeCompare(b.group_code || "")),
    [groups]
  );

  const counts = useMemo(() => {
    const c = { draft: 0, accepted: 0, archived: archivedGroups.length };
    activeGroups.forEach((g) => {
      const s = normalizeStatus(g.status);
      if (s === "accepted") c.accepted += 1;
      else c.draft += 1;
    });
    return c;
  }, [activeGroups, archivedGroups]);

  const visibleGroups = view === "archived" ? archivedGroups : activeGroups;

  const moveTargets = useMemo(
    () =>
      activeGroups.map((g) => ({
        id: g.id,
        label: `${g.group_code} — ${g.title}`,
      })),
    [activeGroups]
  );

  const setStatus = async (group, status) => {
    try {
      // Archive flow keeps the existing detach-findings behaviour so
      // findings are not orphaned permanently. Draft / accepted
      // transitions are simple status flips.
      if (status === "archived") {
        for (const fid of group.linked_finding_ids || []) {
          await ax.post(`/priva/findings/${fid}/reassign`, {
            source_group_id: group.id,
            target_group_id: null,
          });
        }
      }
      await ax.patch(`/priva/groups/${group.id}`, { status });
      const label =
        status === "accepted"
          ? "Accepted"
          : status === "archived"
          ? "Archived"
          : "Moved to Draft";
      toast.success(`${group.group_code} · ${label}`);
      if (status === "archived" && expandedId === group.id) setExpandedId(null);
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Status update failed");
    }
  };

  const handleRename = async ({ title, priva_summary }) => {
    if (!renameDialog) return;
    try {
      await ax.patch(`/priva/groups/${renameDialog.group.id}`, {
        title,
        priva_summary,
      });
      toast.success("Workstream updated");
      setRenameDialog(null);
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Update failed");
    }
  };

  const handleMerge = async (targetId) => {
    if (!mergeDialog) return;
    try {
      await ax.post(`/priva/groups/${mergeDialog.group.id}/merge`, {
        target_group_id: targetId,
      });
      toast.success("Workstreams merged");
      setMergeDialog(null);
      setExpandedId(targetId);
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Merge failed");
    }
  };

  const handleMoveFinding = async (group, findingId, targetGroupId) => {
    try {
      if (targetGroupId === "__create__") {
        setCreateDialog({ pendingFindingId: findingId, sourceGroupId: group.id });
        return;
      }
      await ax.post(`/priva/findings/${findingId}/reassign`, {
        source_group_id: group.id,
        target_group_id: targetGroupId,
      });
      toast.success("Finding moved");
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Move failed");
    }
  };

  const handleRemoveFinding = async (group, findingId) => {
    try {
      await ax.post(`/priva/findings/${findingId}/reassign`, {
        source_group_id: group.id,
        target_group_id: null,
      });
      toast.success("Finding removed from workstream");
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Remove failed");
    }
  };

  const handleEditRootCause = async (findingId, newText) => {
    // Consultant override of the AI-generated probable root cause. The
    // backend flips `ai_root_cause_edited` automatically when the text
    // differs from the prior value so the UI can render an "edited" hint.
    try {
      await ax.put(`/issues/${findingId}`, { ai_root_cause: newText });
      toast.success("Probable root cause updated");
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Update failed");
      throw e;
    }
  };

  const handleEditImplementation = async (findingId, patch) => {
    // Step 1 (June 2026) — save per-finding Implementation classification
    // (WRICEF type / complexity / rationale). The backend validates the
    // values and flips only the changed field's `*_edited` flag.
    try {
      await ax.put(`/issues/${findingId}`, patch);
      toast.success("Implementation classification updated");
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Update failed");
      throw e;
    }
  };

  const handleCreateAndMove = async ({ title, priva_summary }) => {
    if (!createDialog || createDialog === true) {
      try {
        const r = await ax.post(`/engagements/${engagementId}/priva/groups`, {
          title,
          priva_summary,
        });
        toast.success(`Created ${r.data?.group_code || "workstream"}`);
        setCreateDialog(false);
        setExpandedId(r.data?.id || null);
        await reload();
      } catch (e) {
        const detail = e?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Create failed");
      }
      return;
    }
    try {
      const r = await ax.post(`/engagements/${engagementId}/priva/groups`, {
        title,
        priva_summary,
      });
      await ax.post(
        `/priva/findings/${createDialog.pendingFindingId}/reassign`,
        {
          source_group_id: createDialog.sourceGroupId,
          target_group_id: r.data?.id,
        }
      );
      toast.success(`Moved into new ${r.data?.group_code}`);
      setCreateDialog(false);
      setExpandedId(r.data?.id || null);
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed");
    }
  };

  const hasExistingWorkstreams = activeGroups.length + archivedGroups.length > 0;

  return (
    <div
      className="max-w-6xl mx-auto space-y-3"
      data-testid="smartimprove-insights-tab"
    >
      <Toolbar
        counts={counts}
        view={view}
        onView={setView}
        generating={aiGenerating}
        isFinalized={isFinalized}
        onGenerate={confirmAndGenerate}
        hasExisting={hasExistingWorkstreams}
      />

      {loading ? (
        <div className="text-xs text-[#6B7280] px-1">Loading workstreams…</div>
      ) : visibleGroups.length === 0 ? (
        view === "archived" ? (
          <ArchivedEmptyState />
        ) : (
          <EmptyState
            onGenerate={confirmAndGenerate}
            generating={aiGenerating}
            isFinalized={isFinalized}
          />
        )
      ) : (
        <div className="space-y-2">
          {visibleGroups.map((g) => (
            <WorkstreamRow
              key={g.id}
              group={g}
              expanded={expandedId === g.id}
              onToggle={() =>
                setExpandedId(expandedId === g.id ? null : g.id)
              }
              findingsById={findingsById}
              sitesById={sitesById}
              moveTargets={moveTargets.filter((t) => t.id !== g.id)}
              isFinalized={isFinalized}
              onRename={() => setRenameDialog({ group: g })}
              onMerge={() => setMergeDialog({ group: g })}
              onStatus={(s) => setStatus(g, s)}
              onMoveFinding={(fid, tid) => handleMoveFinding(g, fid, tid)}
              onRemoveFinding={(fid) => handleRemoveFinding(g, fid)}
              onEditRootCause={handleEditRootCause}
              onEditImplementation={handleEditImplementation}
            />
          ))}
        </div>
      )}

      {renameDialog ? (
        <RenameDialog
          group={renameDialog.group}
          onClose={() => setRenameDialog(null)}
          onSave={handleRename}
        />
      ) : null}
      {createDialog ? (
        <CreateDialog
          isMoveContext={createDialog !== true}
          onClose={() => setCreateDialog(false)}
          onSave={handleCreateAndMove}
        />
      ) : null}
      {mergeDialog ? (
        <MergeDialog
          group={mergeDialog.group}
          options={moveTargets.filter((t) => t.id !== mergeDialog.group.id)}
          onClose={() => setMergeDialog(null)}
          onConfirm={handleMerge}
        />
      ) : null}
      {regenConfirm ? (
        <RegenerateConfirmDialog
          counts={counts}
          onClose={() => setRegenConfirm(false)}
          onConfirm={runGenerate}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------- toolbar --

function Toolbar({
  counts,
  view,
  onView,
  generating,
  isFinalized,
  onGenerate,
  hasExisting,
}) {
  return (
    <section
      className="bg-white border border-[#E5E7EB] rounded-sm px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
      data-testid="insights-header"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Sparkles className="w-4 h-4 text-[#6E5BCE] shrink-0" />
        <h2
          className="text-sm font-semibold text-[#111827]"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          Transformation Workstreams
        </h2>
        <span
          className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F1F5F9] text-[#475569]"
          data-testid="insights-count-draft"
          title="Draft workstreams"
        >
          {counts.draft} draft
        </span>
        <span
          className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#ECFDF5] text-[#047857] border border-[#A7F3D0]"
          data-testid="insights-count-accepted"
          title="Accepted workstreams"
        >
          {counts.accepted} accepted
        </span>
        <span className="text-[11px] text-[#6B7280] hidden md:inline truncate ml-1">
          · Review, refine, then Accept workstreams before generating
          recommendations.
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <div
          className="inline-flex rounded-sm border border-[#E5E7EB] overflow-hidden"
          data-testid="insights-view-switch"
        >
          <button
            type="button"
            onClick={() => onView("active")}
            className={`px-2.5 h-7 text-[11px] font-semibold transition-colors ${
              view === "active"
                ? "bg-[#F0EDF9] text-[#473F8B]"
                : "bg-white text-[#6B7280] hover:bg-[#F8FAFC]"
            }`}
            data-testid="insights-view-active"
          >
            Active
          </button>
          <button
            type="button"
            onClick={() => onView("archived")}
            className={`px-2.5 h-7 text-[11px] font-semibold border-l border-[#E5E7EB] transition-colors ${
              view === "archived"
                ? "bg-[#FEF2F2] text-[#B91C1C]"
                : "bg-white text-[#6B7280] hover:bg-[#F8FAFC]"
            }`}
            data-testid="insights-view-archived"
          >
            Archived ({counts.archived})
          </button>
        </div>
        <Button
          onClick={onGenerate}
          disabled={generating || isFinalized}
          className="h-7 bg-[#6E5BCE] hover:bg-[#5847B0] text-white rounded-sm text-xs transition-colors"
          data-testid="insights-generate-btn"
        >
          <RefreshCw
            className={`w-3.5 h-3.5 mr-1 ${generating ? "animate-spin" : ""}`}
          />
          {generating
            ? "Generating…"
            : hasExisting
            ? "Re-generate Improvement Analysis"
            : "Generate Improvement Analysis"}
        </Button>
      </div>
    </section>
  );
}

function EmptyState({ onGenerate, generating, isFinalized }) {
  return (
    <div
      className="bg-white border border-dashed border-[#CBD5E1] rounded-sm px-6 py-10 text-center"
      data-testid="insights-empty-state"
    >
      <div className="w-10 h-10 rounded-full bg-[#F0EDF9] mx-auto mb-3 flex items-center justify-center">
        <Wand2 className="w-5 h-5 text-[#6E5BCE]" />
      </div>
      <p className="text-sm font-semibold text-[#111827]">
        No AI Analysis Generated Yet
      </p>
      <p className="text-xs text-[#6B7280] mt-1 max-w-md mx-auto">
        Capture findings in Operational Findings, then let PRIVA analyze
        operational issues and group related findings into transformation
        workstreams automatically.
      </p>
      <div className="mt-4 flex items-center justify-center">
        <Button
          onClick={onGenerate}
          disabled={generating || isFinalized}
          className="h-8 bg-[#6E5BCE] hover:bg-[#5847B0] text-white rounded-sm text-xs px-4 transition-colors"
          data-testid="insights-empty-generate-btn"
        >
          <Sparkles className="w-3.5 h-3.5 mr-1.5" />
          {generating ? "Generating…" : "Generate Improvement Analysis"}
        </Button>
      </div>
    </div>
  );
}

function ArchivedEmptyState() {
  return (
    <div
      className="bg-white border border-dashed border-[#CBD5E1] rounded-sm px-6 py-10 text-center"
      data-testid="insights-archived-empty"
    >
      <div className="w-10 h-10 rounded-full bg-[#FEF2F2] mx-auto mb-3 flex items-center justify-center">
        <Archive className="w-5 h-5 text-[#B91C1C]" />
      </div>
      <p className="text-sm font-semibold text-[#111827]">No archived workstreams</p>
      <p className="text-xs text-[#6B7280] mt-1 max-w-md mx-auto">
        Archived workstreams will appear here. You can restore them at any time.
      </p>
    </div>
  );
}

// ---------------------------------------------------- workstream row --

function WorkstreamRow({
  group,
  expanded,
  onToggle,
  findingsById,
  sitesById,
  moveTargets,
  isFinalized,
  onRename,
  onMerge,
  onStatus,
  onMoveFinding,
  onRemoveFinding,
  onEditRootCause,
  onEditImplementation,
}) {
  const status = normalizeStatus(group.status);
  const linked = useMemo(() => {
    return (group.linked_finding_ids || [])
      .map((id) => findingsById[id])
      .filter(Boolean)
      .sort(compareFindings);
  }, [group, findingsById]);

  const codes = linked.map((f) => f.finding_code).filter(Boolean);
  const findingCount = linked.length;
  const siteCount = (group.affected_site_ids || []).length;

  return (
    <article
      className={`bg-white border rounded-sm transition ${
        expanded
          ? "border-[#6E5BCE] shadow-sm"
          : "border-[#E5E7EB] hover:border-[#94A3B8]"
      }`}
      data-testid={`insights-card-${group.id}`}
    >
      {/* Collapsed header — always visible */}
      <header className="px-4 py-3 flex items-start gap-2">
        <button
          type="button"
          onClick={onToggle}
          className="h-6 w-6 mt-0.5 inline-flex items-center justify-center text-[#473F8B] hover:bg-[#F0EDF9] rounded-sm shrink-0"
          data-testid={`insights-expand-${group.id}`}
          aria-label={expanded ? "Collapse workstream" : "Expand workstream"}
        >
          {expanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>
        <span
          className="shrink-0 text-[10px] font-bold tracking-wider text-[#025979] bg-[#F0F7FA] border border-[#BAE6FD] px-1.5 py-0.5 rounded-sm whitespace-nowrap mt-0.5"
          title="Workstream code"
        >
          {group.group_code}
        </span>
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 min-w-0 text-left"
          data-testid={`insights-toggle-${group.id}`}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-semibold text-[#111827]">{group.title}</h4>
            <span
              className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border rounded-sm ${
                STATUS_BADGE[status] || STATUS_BADGE.draft
              }`}
              data-testid={`insights-status-${group.id}`}
            >
              {status}
            </span>
          </div>
          {group.priva_summary ? (
            <p className="text-[12px] text-[#374151] mt-1 leading-snug line-clamp-2">
              {group.priva_summary}
            </p>
          ) : null}
          {codes.length ? (
            <div
              className="mt-2 flex items-center gap-1.5 flex-wrap"
              data-testid={`insights-finding-codes-${group.id}`}
            >
              {codes.slice(0, expanded ? codes.length : 10).map((c) => (
                <span
                  key={c}
                  className="text-[10px] font-bold tracking-wider text-[#025979] bg-[#F0F7FA] border border-[#BAE6FD] px-1.5 py-0.5 rounded-sm"
                >
                  {c}
                </span>
              ))}
              {!expanded && codes.length > 10 ? (
                <span className="text-[10px] text-[#6B7280]">
                  +{codes.length - 10} more
                </span>
              ) : null}
            </div>
          ) : (
            <p className="mt-2 text-[11px] text-[#94A3B8] italic">
              No findings linked. Move findings here from other workstreams.
            </p>
          )}
          <div className="mt-2 flex items-center gap-3 text-[11px] text-[#6B7280]">
            <span>
              <strong className="text-[#374151]">{findingCount}</strong>{" "}
              {findingCount === 1 ? "finding" : "findings"}
            </span>
            <span>·</span>
            <span>
              <strong className="text-[#374151]">{siteCount}</strong>{" "}
              {siteCount === 1 ? "warehouse" : "warehouses"}
            </span>
          </div>
        </button>
        <div className="flex items-center gap-1 shrink-0">
          {status !== "archived" ? (
            <button
              type="button"
              onClick={onRename}
              disabled={isFinalized}
              className="h-7 w-7 inline-flex items-center justify-center text-[#473F8B] hover:bg-[#F0EDF9] rounded-sm disabled:opacity-40"
              data-testid={`insights-rename-${group.id}`}
              aria-label="Rename workstream"
              title="Rename workstream"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          ) : null}
        </div>
      </header>

      {/* Expanded body */}
      {expanded ? (
        <ExpandedBody
          status={status}
          group={group}
          linked={linked}
          sitesById={sitesById}
          moveTargets={moveTargets}
          isFinalized={isFinalized}
          onMerge={onMerge}
          onStatus={onStatus}
          onMoveFinding={onMoveFinding}
          onRemoveFinding={onRemoveFinding}
          onEditRootCause={onEditRootCause}
          onEditImplementation={onEditImplementation}
        />
      ) : null}
    </article>
  );
}

function ExpandedBody({
  status,
  group,
  linked,
  sitesById,
  moveTargets,
  isFinalized,
  onMerge,
  onStatus,
  onMoveFinding,
  onRemoveFinding,
  onEditRootCause,
  onEditImplementation,
}) {
  const isArchived = status === "archived";

  return (
    <div className="border-t border-[#E5E7EB]">
      <div className="px-4 py-3 flex items-center justify-end gap-2 bg-[#F8FAFC] flex-wrap">
        {!isArchived ? (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={onMerge}
              disabled={isFinalized || moveTargets.length === 0}
              className="h-7 rounded-sm text-xs border-[#E5E7EB] text-[#374151] hover:border-[#6E5BCE] hover:text-[#473F8B]"
              data-testid={`insights-merge-${group.id}`}
            >
              <GitMerge className="w-3.5 h-3.5 mr-1" />
              Merge
            </Button>
            {status === "draft" ? (
              <Button
                onClick={() => onStatus("accepted")}
                disabled={isFinalized || linked.length === 0}
                className="h-7 bg-[#047857] hover:bg-[#065F46] text-white rounded-sm text-xs"
                data-testid={`insights-accept-${group.id}`}
                title={
                  linked.length === 0
                    ? "Link at least one finding before accepting"
                    : "Mark as Accepted — feeds recommendations"
                }
              >
                <CheckCircle className="w-3.5 h-3.5 mr-1" />
                Accept
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onStatus("draft")}
                disabled={isFinalized}
                className="h-7 rounded-sm text-xs border-[#E5E7EB] text-[#374151] hover:border-[#6E5BCE] hover:text-[#473F8B]"
                data-testid={`insights-revert-${group.id}`}
              >
                <Undo2 className="w-3.5 h-3.5 mr-1" />
                Revert to Draft
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (
                  window.confirm(
                    `Archive ${group.group_code}? Findings will be detached.`
                  )
                ) {
                  onStatus("archived");
                }
              }}
              disabled={isFinalized}
              className="h-7 rounded-sm text-xs border-[#E5E7EB] text-[#B91C1C] hover:border-[#B91C1C] hover:bg-[#FEF2F2]"
              data-testid={`insights-archive-${group.id}`}
            >
              <Archive className="w-3.5 h-3.5 mr-1" />
              Archive
            </Button>
          </>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onStatus("draft")}
            disabled={isFinalized}
            className="h-7 rounded-sm text-xs border-[#E5E7EB] text-[#374151] hover:border-[#6E5BCE] hover:text-[#473F8B]"
            data-testid={`insights-restore-${group.id}`}
          >
            <ArchiveRestore className="w-3.5 h-3.5 mr-1" />
            Restore to Draft
          </Button>
        )}
      </div>

      {group.priva_root_cause ? (
        <div
          className="px-4 pt-3"
          data-testid={`insights-rootcause-${group.id}`}
        >
          <h5 className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#374151] mb-1 inline-flex items-center gap-1.5">
            Probable Root Cause
            <span
              className="inline-flex items-center gap-1 text-[9px] font-bold tracking-wider text-[#473F8B] bg-[#F0EDF9] border border-[#DDD6FE] px-1 py-0.5 rounded-sm uppercase"
              title="AI-generated by PRIVA"
            >
              <Sparkles className="w-2.5 h-2.5" />
              PRIVA
            </span>
          </h5>
          <p className="text-[13px] text-[#374151] leading-snug">
            {group.priva_root_cause}
          </p>
        </div>
      ) : null}

      <div className="px-4 py-3">
        <h5 className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#473F8B] mb-2">
          Linked Findings
        </h5>
        {linked.length === 0 ? (
          <p className="text-xs text-[#6B7280] italic">
            No findings linked. Move findings here from other workstreams or
            re-generate the Improvement Analysis from Operational Findings.
          </p>
        ) : (
          <div className="overflow-x-auto border border-[#E5E7EB] rounded-sm">
            <table
              className="w-full text-sm"
              data-testid={`insights-findings-table-${group.id}`}
            >
              <thead className="bg-[#F8FAFC] text-left text-[10px] uppercase tracking-wider text-[#6B7280]">
                <tr>
                  <th className="px-3 py-2 font-semibold">Finding ID</th>
                  <th className="px-3 py-2 font-semibold min-w-[280px]">Finding</th>
                  <th className="px-3 py-2 font-semibold">Priority</th>
                  <th className="px-3 py-2 font-semibold">Warehouses</th>
                  {!isArchived ? (
                    <th className="px-3 py-2 font-semibold text-right">Actions</th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {linked.map((f) => {
                  const priority = f.priority || f.severity || "medium";
                  const wh = (f.applicable_site_ids || [])
                    .map((sid) => sitesById[sid])
                    .filter(Boolean);
                  return (
                    <tr
                      key={f.id}
                      className="border-t border-[#F1F5F9]"
                      data-testid={`insights-row-${group.id}-${f.id}`}
                    >
                      <td className="px-3 py-2">
                        <span className="inline-flex text-[10px] font-bold tracking-wider text-[#025979] bg-[#F0F7FA] border border-[#BAE6FD] px-1.5 py-0.5 rounded-sm whitespace-nowrap">
                          {f.finding_code || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-[13px] text-[#111827]">
                        <div>{f.title}</div>
                        {f.ai_root_cause ? (
                          <RootCauseCell
                            finding={f}
                            isFinalized={isFinalized}
                            isArchived={isArchived}
                            onSave={(text) => onEditRootCause(f.id, text)}
                          />
                        ) : null}
                        <ImplementationCell
                          finding={f}
                          canEdit={!isFinalized && !isArchived}
                          onSave={(patch) => onEditImplementation(f.id, patch)}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm capitalize ${
                            PRIORITY_CHIP[priority] || ""
                          }`}
                        >
                          {priority}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-[#374151]">
                        {wh.length === 0 ? (
                          <span className="text-[#94A3B8]">—</span>
                        ) : wh.length <= 2 ? (
                          wh.join(", ")
                        ) : (
                          <span>
                            {wh.slice(0, 2).join(", ")}{" "}
                            <span className="text-[#94A3B8]">
                              +{wh.length - 2}
                            </span>
                          </span>
                        )}
                      </td>
                      {!isArchived ? (
                        <td className="px-3 py-2 text-right">
                          <FindingActions
                            findingCode={f.finding_code}
                            moveTargets={moveTargets}
                            disabled={isFinalized}
                            onMove={(tid) => onMoveFinding(f.id, tid)}
                            onRemove={() => onRemoveFinding(f.id)}
                          />
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function RootCauseCell({ finding, isFinalized, isArchived, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(finding.ai_root_cause || "");
  const [saving, setSaving] = useState(false);
  const canEdit = !isFinalized && !isArchived;
  const wasEdited = Boolean(finding.ai_root_cause_edited);

  const start = () => {
    setDraft(finding.ai_root_cause || "");
    setEditing(true);
  };
  const cancel = () => {
    setDraft(finding.ai_root_cause || "");
    setEditing(false);
  };
  const save = async () => {
    const next = draft.trim();
    if (!next || next === (finding.ai_root_cause || "").trim()) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } catch (_e) {
      // toast handled in parent
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div
        className="mt-1 flex items-start gap-1.5"
        data-testid={`finding-rootcause-edit-${finding.id}`}
      >
        <span
          className="inline-flex items-center gap-1 text-[9px] font-bold tracking-wider text-[#473F8B] bg-[#F0EDF9] border border-[#DDD6FE] px-1 py-0.5 rounded-sm uppercase shrink-0 mt-0.5"
          title="AI-generated by PRIVA"
        >
          <Sparkles className="w-2.5 h-2.5" />
          PRIVA
        </span>
        <div className="flex-1 min-w-0">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            disabled={saving}
            className="w-full text-[12px] border-[#E5E7EB] focus-visible:ring-[#6E5BCE] resize-none"
            data-testid={`finding-rootcause-input-${finding.id}`}
          />
          <div className="mt-1 flex items-center gap-1.5">
            <Button
              size="sm"
              onClick={save}
              disabled={saving || !draft.trim()}
              className="h-6 px-2 text-[11px] bg-[#025979] hover:bg-[#024864] text-white rounded-sm"
              data-testid={`finding-rootcause-save-${finding.id}`}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={cancel}
              disabled={saving}
              className="h-6 px-2 text-[11px] border-[#E5E7EB] text-[#374151] rounded-sm"
              data-testid={`finding-rootcause-cancel-${finding.id}`}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mt-1 flex items-start gap-1.5 group/rc"
      data-testid={`finding-rootcause-${finding.id}`}
    >
      <span
        className="inline-flex items-center gap-1 text-[9px] font-bold tracking-wider text-[#473F8B] bg-[#F0EDF9] border border-[#DDD6FE] px-1 py-0.5 rounded-sm uppercase shrink-0 mt-0.5"
        title={
          wasEdited
            ? "AI-generated by PRIVA · edited by consultant"
            : "AI-generated by PRIVA"
        }
      >
        <Sparkles className="w-2.5 h-2.5" />
        PRIVA
      </span>
      <span className="text-[11px] text-[#6B7280] italic leading-snug flex-1">
        <span className="font-semibold not-italic text-[#374151]">
          Probable root cause:
        </span>{" "}
        {finding.ai_root_cause}
        {wasEdited ? (
          <span
            className="ml-1 not-italic text-[10px] font-medium text-[#94A3B8]"
            title={
              finding.ai_root_cause_edited_at
                ? `Edited ${new Date(finding.ai_root_cause_edited_at).toLocaleString()}`
                : "Edited by consultant"
            }
          >
            (edited)
          </span>
        ) : null}
      </span>
      {canEdit ? (
        <button
          type="button"
          onClick={start}
          className="opacity-0 group-hover/rc:opacity-100 transition-opacity h-5 w-5 inline-flex items-center justify-center text-[#473F8B] hover:bg-[#F0EDF9] rounded-sm shrink-0"
          aria-label="Edit probable root cause"
          title="Edit probable root cause"
          data-testid={`finding-rootcause-edit-btn-${finding.id}`}
        >
          <Pencil className="w-3 h-3" />
        </button>
      ) : null}
    </div>
  );
}

// Step 1 (June 2026) — per-finding Implementation classification. Reuses the
// existing SmartImprove WRICEF taxonomy; NOT auto-populated yet (blank until
// the PRIVA prompt step). Consultant-editable inline inside Improvement
// Insights only — never added to the Operational Findings capture screen.
const IMPL_WRICEF_TYPE_OPTIONS = [
  "Config",
  "Report",
  "Interface",
  "Conversion",
  "Enhancement",
  "Form",
  "Workflow",
  "Mixed",
];
const IMPL_COMPLEXITY_OPTIONS = ["Low", "Medium", "High", "Very High"];

function ImplementationCell({ finding, canEdit, onSave }) {
  const savedType = finding.ai_implementation_wricef_type || "";
  const savedComplexity = finding.ai_implementation_complexity || "";
  const savedRationale = finding.ai_implementation_rationale || "";

  const [type, setType] = useState(savedType);
  const [complexity, setComplexity] = useState(savedComplexity);
  const [rationale, setRationale] = useState(savedRationale);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showFullRationale, setShowFullRationale] = useState(false);

  useEffect(() => {
    setType(savedType);
    setComplexity(savedComplexity);
    setRationale(savedRationale);
  }, [savedType, savedComplexity, savedRationale]);

  const dirty =
    type !== savedType ||
    complexity !== savedComplexity ||
    rationale.trim() !== savedRationale.trim();

  const save = async () => {
    if (!dirty) {
      setEditing(false);
      return;
    }
    const patch = {};
    if (type !== savedType) patch.ai_implementation_wricef_type = type;
    if (complexity !== savedComplexity)
      patch.ai_implementation_complexity = complexity;
    if (rationale.trim() !== savedRationale.trim())
      patch.ai_implementation_rationale = rationale.trim();
    setSaving(true);
    try {
      await onSave(patch);
      setEditing(false);
    } catch (_e) {
      // toast handled in parent
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setType(savedType);
    setComplexity(savedComplexity);
    setRationale(savedRationale);
    setEditing(false);
  };

  const anyEdited =
    finding.ai_implementation_wricef_type_edited ||
    finding.ai_implementation_complexity_edited ||
    finding.ai_implementation_rationale_edited;
  const rationaleLong = savedRationale.length > 160;

  return (
    <div
      className="mt-2 pl-2 border-l border-[#E5E7EB]"
      data-testid={`finding-impl-${finding.id}`}
    >
      <div className="flex items-center gap-1.5">
        <h6 className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#6B7280]">
          Implementation
        </h6>
        {anyEdited ? (
          <span className="text-[9px] font-medium text-[#94A3B8]">(edited)</span>
        ) : null}
        {canEdit && !editing ? (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="h-5 w-5 inline-flex items-center justify-center text-[#473F8B] hover:bg-[#F0EDF9] rounded-sm"
            data-testid={`finding-impl-edit-${finding.id}`}
            aria-label="Edit implementation classification"
            title="Edit implementation classification"
          >
            <Pencil className="w-3 h-3" />
          </button>
        ) : null}
      </div>

      {editing ? (
        <>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-[#6B7280]">Type</span>
              <Select
                value={type || undefined}
                onValueChange={setType}
                disabled={saving}
              >
                <SelectTrigger
                  className="h-7 w-[130px] text-[11px] rounded-sm border-[#E5E7EB] bg-white"
                  data-testid={`finding-impl-type-${finding.id}`}
                >
                  <SelectValue placeholder="Unclassified" />
                </SelectTrigger>
                <SelectContent>
                  {IMPL_WRICEF_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o} value={o} className="text-[12px]">
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-[#6B7280]">Complexity</span>
              <Select
                value={complexity || undefined}
                onValueChange={setComplexity}
                disabled={saving}
              >
                <SelectTrigger
                  className="h-7 w-[120px] text-[11px] rounded-sm border-[#E5E7EB] bg-white"
                  data-testid={`finding-impl-complexity-${finding.id}`}
                >
                  <SelectValue placeholder="Unclassified" />
                </SelectTrigger>
                <SelectContent>
                  {IMPL_COMPLEXITY_OPTIONS.map((o) => (
                    <SelectItem key={o} value={o} className="text-[12px]">
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-1.5">
            <Textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              rows={3}
              disabled={saving}
              placeholder="Rationale (optional)…"
              className="w-full text-[12px] border-[#E5E7EB] focus-visible:ring-[#6E5BCE] resize-none bg-white"
              data-testid={`finding-impl-rationale-${finding.id}`}
            />
          </div>
          <div className="mt-1.5 flex items-center gap-1.5">
            <Button
              size="sm"
              onClick={save}
              disabled={saving}
              className="h-6 px-2 text-[11px] bg-[#025979] hover:bg-[#024864] text-white rounded-sm"
              data-testid={`finding-impl-save-${finding.id}`}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={cancel}
              disabled={saving}
              className="h-6 px-2 text-[11px] border-[#E5E7EB] text-[#374151] rounded-sm"
              data-testid={`finding-impl-cancel-${finding.id}`}
            >
              Cancel
            </Button>
          </div>
        </>
      ) : (
        <div className="mt-0.5">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[12px] text-[#374151]">
            <span data-testid={`finding-impl-type-view-${finding.id}`}>
              <span className="text-[#6B7280]">Type: </span>
              {savedType || (
                <span className="text-[#94A3B8] italic">Unclassified</span>
              )}
            </span>
            <span data-testid={`finding-impl-complexity-view-${finding.id}`}>
              <span className="text-[#6B7280]">Complexity: </span>
              {savedComplexity || (
                <span className="text-[#94A3B8] italic">Unclassified</span>
              )}
            </span>
          </div>
          {savedRationale ? (
            <div className="mt-0.5">
              <p
                className={`text-[12px] text-[#6B7280] leading-snug ${
                  showFullRationale ? "" : "line-clamp-2"
                }`}
                data-testid={`finding-impl-rationale-text-${finding.id}`}
              >
                {savedRationale}
              </p>
              {rationaleLong ? (
                <button
                  type="button"
                  onClick={() => setShowFullRationale((v) => !v)}
                  className="mt-0.5 text-[11px] font-medium text-[#473F8B] hover:underline"
                  data-testid={`finding-impl-showmore-${finding.id}`}
                >
                  {showFullRationale ? "Show less" : "Show more"}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}


function FindingActions({ findingCode, moveTargets, disabled, onMove, onRemove }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="h-7 w-7 inline-flex items-center justify-center text-[#473F8B] hover:bg-[#F0EDF9] rounded-sm disabled:opacity-40"
          data-testid={`finding-actions-${findingCode}`}
          aria-label="Finding actions"
        >
          <MoreHorizontal className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-56"
        data-testid={`finding-actions-menu-${findingCode}`}
      >
        <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-[#6B7280]">
          Move to
        </div>
        {moveTargets.length === 0 ? (
          <DropdownMenuItem disabled className="text-xs italic text-[#94A3B8]">
            No other workstreams
          </DropdownMenuItem>
        ) : (
          moveTargets.map((t) => (
            <DropdownMenuItem
              key={t.id}
              onClick={() => onMove(t.id)}
              className="text-xs cursor-pointer"
              data-testid={`finding-move-${findingCode}-${t.id}`}
            >
              <ArrowRightLeft className="w-3.5 h-3.5 mr-2" />
              {t.label}
            </DropdownMenuItem>
          ))
        )}
        <DropdownMenuItem
          onClick={() => onMove("__create__")}
          className="text-xs cursor-pointer"
          data-testid={`finding-move-create-${findingCode}`}
        >
          <Plus className="w-3.5 h-3.5 mr-2" />
          Create new workstream…
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onRemove}
          className="text-xs cursor-pointer text-[#B91C1C] focus:text-[#B91C1C]"
          data-testid={`finding-remove-${findingCode}`}
        >
          <Trash2 className="w-3.5 h-3.5 mr-2" />
          Remove from workstream
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// -------------------------------------------------------- dialogs --

function RenameDialog({ group, onClose, onSave }) {
  const [title, setTitle] = useState(group.title || "");
  const [summary, setSummary] = useState(group.priva_summary || "");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="rename-dialog">
        <DialogHeader>
          <DialogTitle>
            Edit {group.group_code} — {group.title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#6B7280]">
              Workstream Name
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 h-8 text-sm rounded-sm"
              data-testid="rename-dialog-title"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#6B7280]">
              Short Summary
            </label>
            <Textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={3}
              className="mt-1 text-sm rounded-sm"
              data-testid="rename-dialog-summary"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            className="h-7 rounded-sm text-xs"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onSave({ title, priva_summary: summary })}
            disabled={!title.trim()}
            className="h-7 bg-[#6E5BCE] hover:bg-[#5847B0] text-white rounded-sm text-xs"
            data-testid="rename-dialog-save"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateDialog({ isMoveContext, onClose, onSave }) {
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="create-dialog">
        <DialogHeader>
          <DialogTitle>
            {isMoveContext ? "Create Workstream & Move Finding" : "Create Workstream"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#6B7280]">
              Workstream Name
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Inbound RF Optimization"
              className="mt-1 h-8 text-sm rounded-sm"
              data-testid="create-dialog-title"
            />
            <p className="text-[10px] text-[#6B7280] mt-1">
              The system will assign the next available WS code automatically.
            </p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#6B7280]">
              Short Summary
            </label>
            <Textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={3}
              placeholder="One-line description for the workshop"
              className="mt-1 text-sm rounded-sm"
              data-testid="create-dialog-summary"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            className="h-7 rounded-sm text-xs"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onSave({ title, priva_summary: summary })}
            disabled={!title.trim()}
            className="h-7 bg-[#6E5BCE] hover:bg-[#5847B0] text-white rounded-sm text-xs"
            data-testid="create-dialog-save"
          >
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MergeDialog({ group, options, onClose, onConfirm }) {
  const [targetId, setTargetId] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="merge-dialog">
        <DialogHeader>
          <DialogTitle>
            Merge {group.group_code} into…
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-[#374151]">
            All findings from <strong>{group.group_code} — {group.title}</strong>{" "}
            will move to the target workstream. The source workstream will be
            archived.
          </p>
          <Select value={targetId} onValueChange={setTargetId}>
            <SelectTrigger
              className="h-8 text-sm rounded-sm"
              data-testid="merge-dialog-target"
            >
              <SelectValue placeholder="Select target workstream…" />
            </SelectTrigger>
            <SelectContent>
              {options.map((o) => (
                <SelectItem key={o.id} value={o.id} data-testid={`merge-target-${o.id}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            className="h-7 rounded-sm text-xs"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(targetId)}
            disabled={!targetId}
            className="h-7 bg-[#6E5BCE] hover:bg-[#5847B0] text-white rounded-sm text-xs"
            data-testid="merge-dialog-confirm"
          >
            Merge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RegenerateConfirmDialog({ counts, onClose, onConfirm }) {
  const totalExisting = counts.draft + counts.accepted + counts.archived;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-lg"
        data-testid="insights-regen-confirm-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-[#B45309]" />
            Replace Existing Workstreams?
          </DialogTitle>
          <DialogDescription>
            This will replace ALL existing workstreams for this engagement,
            including draft, accepted, edited, merged, and archived
            workstreams. Recommendations linked to these workstreams will
            also be cleared.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm text-[#374151]">
          {totalExisting > 0 ? (
            <>
              <p>The following will be deleted:</p>
              <ul className="text-xs space-y-1 pl-1">
                <li className="flex items-center gap-2">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#6B7280]" />
                  <strong>{counts.draft}</strong> draft workstream
                  {counts.draft === 1 ? "" : "s"}
                </li>
                <li className="flex items-center gap-2">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#047857]" />
                  <strong>{counts.accepted}</strong> accepted workstream
                  {counts.accepted === 1 ? "" : "s"}
                </li>
                <li className="flex items-center gap-2">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#B91C1C]" />
                  <strong>{counts.archived}</strong> archived workstream
                  {counts.archived === 1 ? "" : "s"}
                </li>
                <li className="flex items-center gap-2">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#9D174D]" />
                  All primary and strategic recommendations linked to these workstreams
                </li>
              </ul>
              <p className="text-xs text-[#92400E] bg-[#FEF3C7] border border-[#FDE68A] rounded-sm px-3 py-2">
                Consultant edits — renames, merges, accepted state — will be
                lost. Run history is preserved for audit. You will need to
                accept new workstreams and re-run Generate Recommendations.
              </p>
            </>
          ) : (
            <p>
              PRIVA will analyse all Accepted operational findings and
              generate a fresh set of transformation workstreams.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            className="h-7 rounded-sm text-xs"
            data-testid="insights-regen-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            className="h-7 bg-[#6E5BCE] hover:bg-[#5847B0] text-white rounded-sm text-xs"
            data-testid="insights-regen-confirm"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" />
            {totalExisting > 0 ? "Replace & Re-generate" : "Generate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
